import React, { useEffect, useState } from "react";
import "./Scss/index.scss";
import { BackgroundImage, Box, Center, Loader } from "@mantine/core";
import { useElementSize, useFullscreen, useTimeout, useViewportSize } from "@mantine/hooks";
import { useProfileDataQuery } from "../../Api/Queries";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import i18next from "../../i18n";
import { OsGuard } from "./Components";
import { Header } from "./Components/Header";
import { useFxIdSdk, useIsMobile } from "./Hooks";
import { HelmetWithChildren } from "../../Components/HelmetWithChildren";
import { Promo } from "./Components/Promo";
import {
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesProfileGetResponse,
	FxIdWebFeaturesProfileGetResponseFeature
} from "../../Api/gen";
import { IFxIdSdkAdapterSocialSettings } from "../../SDK/FxIdSdkBaseAdapter";
import { useWebPlayLoginState } from "../../Stores";
import { getAuthToken } from "../../Api/Auth";
import { useGlobalMessagesQueue } from "../../Stores";
import { useInstallPrompt } from "./Hooks/useInstallPrompt";
import { GlobalMessage } from "../../Components/GlobalMessages";
import { useUserStatus } from "../../Utils/Hooks/useUserStatus";
import { USER_STATUSES_WITH_LOGIN_ENABLED } from "../../Utils/Functions";
import { useShowHeader } from "./UseShowHeader";
import { useNormalizedViewportSize } from "./UseNormalizedViewportSize";
import Game from "./Components/Game";
import { apiBasePath } from "@app/constants";
import { webPlayerStore } from "./store/webPlayerStore";

type WebPlayerProps = {
	disableInstallation?: boolean;
	disableVersionSelect?: boolean;
	disableStoreLink?: boolean;
	disableUi?: boolean;
	singleGameSite: boolean;
};

interface WebPlayerInternalProps extends WebPlayerProps {
	socialSettings: IFxIdSdkAdapterSocialSettings;
	currentAccountId: number;
}

const GLOBAL_MESSAGE_TIMEOUT_MS = 40000;

const isGameConnected = (profileData?: FxIdWebFeaturesProfileGetResponse, game?: string) => {
	return Boolean(profileData?.ConnectedGames.find((g) => g.Game === game));
};

const GameBackground = (props: WebPlayerInternalProps & { children: React.ReactNode }) => {
	const gameConfig = webPlayerStore((state) => state.gameConfig);

	if (!gameConfig.PublicWebClientConfig.Background) {
		return <>{props.children}</>;
	}

	let backgroundStyles = {};

	switch (gameConfig.PublicWebClientConfig.Background.Type) {
		case "fullscreen":
			backgroundStyles = {
				backgroundRepeat: "no-repeat",
				backgroundSize: "cover"
			};
			break;
		case "tiling":
			backgroundStyles = {
				backgroundRepeat: "repeat",
				backgroundSize: "auto"
			};
			break;
		case "centered":
			backgroundStyles = {
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				backgroundSize: "contain",
				backgroundPosition: "center center",
				backgroundRepeat: "no-repeat"
			};
			break;
		default:
			break;
	}

	return (
		<BackgroundImage
			src={gameConfig.PublicWebClientConfig.Background.Image}
			sx={{
				height: "100%",
				width: "100%",
				// position: "absolute",
				// zIndex: -100,
				...backgroundStyles
			}}
		>
			{props.children}
		</BackgroundImage>
	);
};

const WebPlayerInternal = (props: WebPlayerInternalProps) => {
	log.info("Rendering web player with props %o", props);
	const { t, i18n } = useTranslation();
	const { disableInstallation, disableVersionSelect, disableUi: disableUiConfig, disableStoreLink } = props;
	const [searchParams, setSearchParams] = useSearchParams();

	const platform = webPlayerStore((state) => state.platform);
	const adapter = webPlayerStore((state) => state.adapter);
	const gameConfig = webPlayerStore((state) => state.gameConfig);

	const { data: profileData, isInitialLoading, isLoadingError } = useProfileDataQuery();
	const { toggle: fullscreenToggle, fullscreen, ref } = useFullscreen();
	const [showGlobalMessage, setShowGlobalMessage] = useState(false);
	const isMobile = useIsMobile();
	const showHeader = useShowHeader();
	const chooseGameResponse = webPlayerStore((state) => state.chooseGameResponse);

	log.info("Search params: %o", searchParams);
	let disableUi = disableUiConfig;

	if (!showHeader) {
		disableUi = true;
	}

	if (searchParams.has("disableUi") && searchParams.get("disableUi") === "1") {
		log.info("Force 'disableUi' from client");
		disableUi = true;
	}

	if (import.meta.env.VITE_APP_DISABLE_UI_OVERRIDE === "1") {
		log.info("Force 'disableUi' from override");
		disableUi = true;
	}

	const { ref: gameContainerRef, width: gameContainerWidth, height: gameContainerHeight } = useElementSize();

	const { height: viewportHeight, width: viewportWidth } = useViewportSize();

	const { height, width } = useNormalizedViewportSize(gameConfig, viewportWidth, viewportHeight);

	const loading = isInitialLoading && !isLoadingError;
	const gameConnected = isGameConnected(profileData, gameConfig.GameSystemName);

	useTimeout(() => setShowGlobalMessage(true), GLOBAL_MESSAGE_TIMEOUT_MS, { autoInvoke: true });

	useEffect(() => {
		if (!profileData) return;
		void i18next.changeLanguage(profileData.Locale || i18next.language || "en");
	}, [profileData]);

	useEffect(() => {
		if (!loading) {
			log.info("Loading done");
			window.prerenderReady = true;
		}
	}, [loading]);

	const onFxId = adapter === "fxid";

	const manifestPublicUrl = encodeURIComponent(import.meta.env.BASE_URL);
	const manifestStartUrl = encodeURIComponent(
		window.location.pathname.endsWith("/") ? window.location.pathname : `${window.location.pathname}/`
	);
	const manifestToken = encodeURIComponent(String(getAuthToken()));
	const manifestUrl = new URL(
		`/api/v1/manifest/${gameConfig.GameSystemName}?PublicUrl=${manifestPublicUrl}&StartUrl=${manifestStartUrl}&Token=${manifestToken}`,
		apiBasePath
	).toString();

	log.info("Loading web player. Platform: %s. Adapter: %s. Width: %s. Height: %s", platform, adapter, width, height);

	log.info("gameConnected", gameConnected);

	//Запрет на вызов контекстного меню
	useEffect(() => {
		log.info("Context menu disabled...");
		document.addEventListener("contextmenu", function (e) {
			e.preventDefault();
		});
	}, []);

	// https://developers.facebook.com/docs/applinks/metadata-reference
	return (
		<>
			<WebPlayerGlobalMessagesController />
			{!loading && (
				<>
					<HelmetWithChildren
						htmlAttributes={{
							prefix:
								'prefix="og: http://ogp.me/ns#\n' +
								"fb: http://ogp.me/ns/fb#\n" +
								'article: http://ogp.me/ns/article#"'
						}}
					>
						<title>{t(`seo.${gameConfig.GameSystemName}.title`)}</title>
						<meta name="description" content={t(`seo.${gameConfig.GameSystemName}.description`)} />

						<meta property="og:title" content={t(`seo.${gameConfig.GameSystemName}.title`)} />
						<meta property="og:description" content={t(`seo.${gameConfig.GameSystemName}.description`)} />
						<meta property="og:url" content={window.location.toString()} />
						<meta property="og:image" content={gameConfig.PublicWebClientConfig.IconUrl} />
						<meta property="og:image:alt" content={t(`seo.${gameConfig.GameSystemName}.title`)} />
						<meta property="og:image:width" content={"512"} />
						<meta property="og:image:height" content={"512"} />
						<meta property="og:site_name" content={t(`seo.${gameConfig.GameSystemName}.title`)} />
						<meta property="og:type" content="article" />
						<meta property="al:web:url" content={window.location.toString()} />
					</HelmetWithChildren>
					{!disableInstallation && (
						<HelmetWithChildren>
							<link rel="manifest" href={manifestUrl} />
						</HelmetWithChildren>
					)}

					{gameConfig.PlatformData.Android && (
						<HelmetWithChildren>
							<meta property="al:android:url" content={gameConfig.PlatformData.Android.AppLinkUrl} />
							<meta property="al:android:package" content={gameConfig.PlatformData.Android.PackageName} />
							<meta
								property="al:android:app_name"
								content={t(`seo.${gameConfig.GameSystemName}.title`)}
							/>
						</HelmetWithChildren>
					)}
					{gameConfig.PlatformData.Ios && (
						<HelmetWithChildren>
							<meta property="al:ios:url" content={gameConfig.PlatformData.Ios.AppLinkUrl} />
							<meta property="al:ios:app_store_id" content={gameConfig.PlatformData.Ios.AppStoreId} />
							<meta property="al:ios:app_name" content={t(`seo.${gameConfig.GameSystemName}.title`)} />
						</HelmetWithChildren>
					)}
					{gameConfig.PlatformData.Windows && (
						<HelmetWithChildren>
							<meta property="al:windows:url" content={gameConfig.PlatformData.Windows.AppLinkUrl} />
							<meta property="al:windows:app_id" content={gameConfig.PlatformData.Windows.AppId} />
							<meta
								property="al:windows:app_name"
								content={t(`seo.${gameConfig.GameSystemName}.title`)}
							/>
							<meta
								property="al:windows_universal:url"
								content={gameConfig.PlatformData.Windows.AppLinkUrl}
							/>
							<meta
								property="al:windows_universal:app_id"
								content={gameConfig.PlatformData.Windows.AppId}
							/>
							<meta
								property="al:windows_universal:app_name"
								content={t(`seo.${gameConfig.GameSystemName}.title`)}
							/>
						</HelmetWithChildren>
					)}
				</>
			)}
			<div
				id="web-player"
				ref={ref}
				className={`noselect web-player${isMobile ? " web-player-mobile" : ""}${
					platform === FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ || adapter === "ymobage"
						? " web-player-wizq"
						: ""
				}`}
				style={{ height: height, width }}
			>
				{!disableUi && showGlobalMessage && <GlobalMessage />}
				<Box pos="relative" sx={{ flexGrow: 1 }} mah="100%" maw="100%">
					{!disableUi && (
						<Header
							profile={profileData}
							fullscreen={fullscreen}
							fullscreenToggle={fullscreenToggle}
							disableStoreLink={disableStoreLink}
							isMainSite={!props.singleGameSite}
						/>
					)}
					<GameBackground {...props}>
						<div className={`game-container${disableUi ? " no-ui" : ""}`} ref={gameContainerRef}>
							{(loading || !gameConnected) && !gameConnected && (
								<div className="content">
									{loading && (
										<div className="loading">
											<Loader />
										</div>
									)}
									{!loading && !gameConnected && onFxId && (
										<>
											<Promo />
										</>
									)}
								</div>
							)}
							{gameConnected && (
								<Game
									disableVersionSelect={
										disableVersionSelect ||
										!profileData?.Features.includes(
											FxIdWebFeaturesProfileGetResponseFeature.ListGames
										)
									}
									width={gameContainerWidth}
									height={gameContainerHeight}
									currentAccountId={props.currentAccountId}
								/>
							)}
						</div>
					</GameBackground>
				</Box>
			</div>
		</>
	);
};

export const WebPlayer = (props: WebPlayerProps) => {
	log.info("Render WebPlayer...");
	const gameConfig = webPlayerStore((state) => state.gameConfig);
	const portalConfig = webPlayerStore((state) => state.portalConfig);
	const adapter = webPlayerStore((state) => state.adapter);

	if (!portalConfig) {
		log.info("portalConfig: ", !!portalConfig);
		throw new Error("конфиг не пришел...");
	}

	const { initialized, socialSettings, currentAccountId } = useFxIdSdk(
		gameConfig.GameSystemName,
		adapter,
		portalConfig
	);

	useEffect(() => {
		if (!document.body.classList.contains("web-play")) document.body.classList.add("web-play");
		document.documentElement.style.overflow = "hidden";

		return () => {
			document.body.classList.remove("web-play");
			document.documentElement.removeAttribute("style");
		};
	}, []);

	log.info(initialized, socialSettings);

	if (!initialized || !socialSettings) {
		return (
			<Center h="100vh">
				<Loader size="xl" />
			</Center>
		);
	}

	return (
		<OsGuard>
			<WebPlayerInternal
				{...props}
				socialSettings={socialSettings}
				disableUi={socialSettings?.ui.disabled}
				currentAccountId={currentAccountId}
			/>
		</OsGuard>
	);
};

const WebPlayerGlobalMessagesController = () => {
	const portalConfig = webPlayerStore((state) => state.portalConfig);
	const adapter = webPlayerStore((state) => state.adapter);
	const gameConfig = webPlayerStore((state) => state.gameConfig);

	const { canInstall, showPrompt } = useInstallPrompt(gameConfig.GameSystemName);
	const [, { enqueue, dequeue }] = useGlobalMessagesQueue();
	const isLoginAvailable = useWebPlayLoginState();
	const userStatus = useUserStatus();

	useProfileDataQuery({
		onSuccess: (data) => {
			dequeue("no-email");
			dequeue("validate-email");
			if (isLoginAvailable && USER_STATUSES_WITH_LOGIN_ENABLED.includes(userStatus)) {
				enqueue({
					id: "no-email",
					color: data.HasCompletedOrder ? "red" : "yellow",
					priority: 1,
					meta: { gameConfig, portalConfig, email: data.Email, adapter }
				});
			}
			if (data.Email && !data.EmailValidated && isLoginAvailable) {
				enqueue({
					id: "validate-email",
					color: data.HasCompletedOrder ? "red" : "yellow",
					priority: 1,
					meta: { gameConfig, portalConfig, email: data.Email, adapter }
				});
			}
		}
	});

	useEffect(() => {
		if (!canInstall) return;
		enqueue({
			id: "install-game",
			color: "blue",
			meta: { showPrompt }
		});
	}, [canInstall, enqueue, showPrompt]);
	return null;
};
